package nl.astraeus.wiki.components

import kotlinx.html.FlowContent
import kotlinx.html.button
import kotlinx.html.classes
import kotlinx.html.div
import kotlinx.html.input
import kotlinx.html.label
import kotlinx.html.style

enum class WFDirection {
  VERTICAL,
  HORIZONTAL,
}

sealed class Wireframe(
  val size: String,
) {

  class Container(
    size: String = "auto",
    val direction: WFDirection = WFDirection.VERTICAL,
    val children: List<Wireframe>,
    val parameters: List<String> = listOf()
  ) : Wireframe(size)

  class Element(
    size: String = "auto",
    val type: String = "text",
    val parameters: List<String> = listOf()
  ) : Wireframe(size)
}

fun MutableList<Wireframe.Container>.pop(): Wireframe.Container? = this.lastOrNull()

fun MutableList<Wireframe.Container>.push(wf: Wireframe.Container) = this.add(wf)

fun String.countIndentation(): Int {
  var index = 0

  while (this.length > index && this[index] == '-') {
    index++
  }

  return index
}

data class ParseState(
  val lines: List<String>,
  var line: Int = 0,
  var currentIndentation: Int = 0,
) {

  fun done(): Boolean = lines.size == line

  fun next() {
    if (line < lines.size) {
      line++
    } else {
      error("There are no more lines!")
    }
  }

  fun current(): ParsedLine {
    val text = lines[line]

    var part = 0
    val parts = text.trim().split(Regex("\\s+"))
    val indentation = text.trim().countIndentation()

    if (indentation > 0) {
      part++
    }

    val size = if (part < parts.size) {
      parts[part++]
    } else {
      ""
    }
    val type = if (part < parts.size) {
      parts[part++]
    } else {
      ""
    }
    val parameters = mutableListOf<String>()
    while (part < parts.size) {
      parameters.add(parts[part++])
    }

    return ParsedLine(indentation, size, type, parameters)
  }
}

data class ParsedLine(
  val indentation: Int,
  val size: String,
  val type: String,
  val parameters: List<String>,
) {
  fun isEmpty(): Boolean = indentation == 0 && size.isEmpty() && type.isEmpty() && parameters.isEmpty()
}

fun parseWireframe(text: String): Wireframe.Container {
  val state = ParseState(text.lines())

  val children = parseChildren(state)

  return Wireframe.Container(children = children, parameters = listOf("bordered"))
}

fun parseChildren(state: ParseState): List<Wireframe> {
  val children = mutableListOf<Wireframe>()
  var done = false

  while (!state.done() && !done) {
    val parsedLine = state.current()

    if (parsedLine.isEmpty()) {
      state.next()
    } else if (parsedLine.indentation < state.currentIndentation) {
      done = true
    } else when (parsedLine.type) {
      "horizontal" -> {
        state.next()
        state.currentIndentation++
        children.add(
          Wireframe.Container(
            parsedLine.size,
            WFDirection.HORIZONTAL,
            parseChildren(state),
            parsedLine.parameters
          )
        )
        state.currentIndentation--
      }

      "vertical" -> {
        state.next()
        state.currentIndentation++
        children.add(
          Wireframe.Container(
            parsedLine.size,
            WFDirection.VERTICAL,
            parseChildren(state),
            parsedLine.parameters
          )
        )
        state.currentIndentation--
      }

      else -> {
        children.add(
          Wireframe.Element(
            parsedLine.size,
            parsedLine.type,
            parsedLine.parameters
          )
        )
        state.next()
      }
    }
  }

  return children
}

fun FlowContent.renderWireframe(text: String) {
  div("wireframe") {
    val parsed = parseWireframe(text)

    renderContainer(parsed)
  }
}

fun FlowContent.renderContainer(container: Wireframe.Container) {
  div("container") {
    if (container.parameters.contains("bordered")) {
      classes += "bordered"
    }
    val sizes = container.children.joinToString(separator = " ") { rf ->
      rf.size
    }
    style = if (container.direction == WFDirection.HORIZONTAL) {
      "display: grid; grid-template-columns: $sizes; gap: 1rem;"
    } else {
      "display: grid; grid-template-rows: $sizes; gap: 1rem;"
    }

    for (child in container.children) {
      when (child) {
        is Wireframe.Container -> renderContainer(child)
        is Wireframe.Element -> {
          when (child.type) {
            "label" -> {
              label {
                +(child.parameters.getOrNull(0) ?: "-")
              }
            }

            "button" -> {
              button {
                +(child.parameters.getOrNull(0) ?: "-")
              }
            }

            "input" -> {
              input {

              }
            }

            "box" -> {
              div("wireframe-box") {

              }
            }

            else -> {
              div {
                +"Unknown type: ${child.type}"
              }
            }
          }
        }
      }
    }

  }
}

